import React, { useState, useCallback, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cx from "classnames";
import {
  DndContext, closestCenter, DragEndEvent, PointerSensor,
  useSensor, useSensors, DragOverlay, DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToWindowEdges } from "@dnd-kit/modifiers";

import { usePromptEditorSection } from "src/query/experiences/promptEditor";
import { PageStructureSection } from "src/services/experiences/promptEditor";
import styles from "./storyLine.module.scss";
import { usePromptEditorContext } from "src/components/app/contexts/promptEditorContext";
import { BLANK_TEMPLATE_IDS } from "src/pages/experiences/constants/storyboard";
import { STORYBOARD_TYPES } from "src/types/experiences/storyboardSettings/storyboard";
import {
  buildCleverstoryUrl,
  buildCreationTargetPath,
} from "src/pages/experiences/utils/cleverstoryRedirect";
import {
  PROMPT_EDITOR_DATA,
  PROMPT_EDITOR_DATA_REQUEST,
} from "src/pages/experiences/constants/cleverstoryMessages";
import _ from "lodash";

interface StoryBlock {
  id: string;
  title: string;
  description: string;
  isNew?: boolean;
  _section?: PageStructureSection;
}

let idCounter = 100;
const newId = () => `b${++idCounter}`;

const sectionToBlock = (section: PageStructureSection): StoryBlock => ({
  id:          section.id   ?? newId(),
  title:       section.name ?? "Untitled",
  description: section.plan  ?? "",
  _section:    section,
});

const blockToSection = (block: StoryBlock): PageStructureSection => ({
  ...(block._section ?? {}),
  id:   block.id,
  name: block.title,
  plan: block.description,
});

const BetweenAdder: React.FC<{ onAdd: () => void }> = ({ onAdd }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className={cx(styles.betweenZone, hovered && styles.betweenZoneActive)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={styles.betweenContent}>
        <div className={styles.betweenLine} />
        <button className={styles.betweenPill} onClick={(e) => { e.stopPropagation(); onAdd(); }}>
          <i className="paperflite-add" /> add block
        </button>
        <div className={styles.betweenLine} />
      </div>
    </div>
  );
};

const InlineAdder: React.FC<{
  onConfirm: (title: string, desc: string) => void;
  onCancel:  () => void;
}> = ({ onConfirm, onCancel }) => {
  const [title, setTitle] = useState("");
  const [desc,  setDesc]  = useState("");

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); if (title.trim()) onConfirm(title.trim(), desc.trim()); }
    if (e.key === "Escape") onCancel();
  };

  return (
    <div className={styles.inlineAdderWrapper}>
      <div className={cx(styles.card, styles.cardEditing)}>
        <div className={styles.cardHeader}>
          <span className={styles.slash}>/</span>
          <input autoFocus className={styles.editTitle} placeholder="Title of the section"
            value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={handleKeyDown} />
        </div>
        <textarea className={styles.editDesc} placeholder="Description of the section"
          value={desc} rows={4} onChange={(e) => setDesc(e.target.value)} onKeyDown={handleKeyDown} />
        <div className={cx(styles.cardFooterWrap, styles.footerOpen)}>
          <div className={styles.cardFooterInner}>
            <div className={styles.cardFooter}>
              <div className={styles.footerActions}>
                <button className={styles.footerBtn} onClick={() => title.trim() && onConfirm(title.trim(), desc.trim())}>
                  <i className="paperflite-tick-unselected" />
                </button>
                <button className={styles.footerBtn} onClick={onCancel}>
                  <i className="paperflite-delete" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SortableBlock: React.FC<{
  block:          StoryBlock;
  onDelete:       (id: string) => void;
  onCopy:         (id: string) => void;
  onUpdate:       (id: string, title: string, desc: string) => void;
  isDragOverlay?: boolean;
  titleOnly?:     boolean;
  disableDelete?: boolean;
}> = ({ block, onDelete, onCopy, onUpdate, isDragOverlay, titleOnly = false, disableDelete = false, }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id });

  const [editing,   setEditing]   = useState(false);
  const [editTitle, setEditTitle] = useState(block.title);
  const [editDesc,  setEditDesc]  = useState(block.description);
  const [hovered,   setHovered]   = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const editingRef = useRef(false);

  useEffect(() => { editingRef.current = editing; }, [editing]);

  const commitEdit = useCallback(() => {
    if (!editingRef.current) return;
    const t = editTitle.trim();
    const d = editDesc.trim();
    if (t) onUpdate(block.id, t, d);
    else { setEditTitle(block.title); setEditDesc(block.description); }
    setEditing(false);
  }, [editTitle, editDesc, block.id, block.title, block.description, onUpdate]);

  useEffect(() => {
    if (!editing) return;
    const handler = (e: MouseEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) commitEdit();
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [editing, commitEdit]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey && (e.currentTarget as HTMLElement).tagName !== "TEXTAREA") {
      e.preventDefault(); commitEdit();
    }
    if (e.key === "Escape") { setEditTitle(block.title); setEditDesc(block.description); setEditing(false); }
  };

  const style: React.CSSProperties = {
    transform:  CSS.Transform.toString(transform),
    transition: transition || "transform 200ms ease",
    opacity:    isDragging ? 0 : 1,
  };

  if (isDragOverlay) {
    return (
      <div className={cx(styles.card, styles.cardDragging)}>
        <div className={styles.cardHeader}>
          <span className={styles.slash}>/</span>
          <span className={styles.cardTitle}>{block.title.toUpperCase()}</span>
        </div>
        <div className={cx(styles.descWrap, styles.descOpen)}>
          <div className={styles.descInner}>
            <p className={styles.cardDesc}>{block.description}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.blockWrapper}
      onPointerEnter={() => setHovered(true)}
      onPointerLeave={() => setHovered(false)}
    >
      {hovered && !editing && !titleOnly && (
        <div className={styles.dragHandle} {...attributes} {...listeners}>
          <i className="paperflite-drag" />
        </div>
      )}

      <div
        ref={(node) => {
          setNodeRef(node);
          cardRef.current = node;
        }}
        style={style}
        className={cx(styles.card, {
          [styles.cardNew]: !!block.isNew,
          [styles.cardHovered]: hovered && !editing && !titleOnly,
          [styles.cardEditing]: editing,
          [styles.cardTitleOnly]: titleOnly,
        })}
        onDoubleClick={() => {
          if (!editing && !titleOnly) {
            setEditTitle(block.title);
            setEditDesc(block.description);
            setEditing(true);
          }
        }}
      >
        {editing ? (
          <>
            <div className={styles.cardHeader}>
              <span className={styles.slash}>/</span>
              <input
                autoFocus
                className={styles.editTitle}
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <textarea
              className={styles.editDesc}
              value={editDesc}
              rows={4}
              onChange={(e) => setEditDesc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") setEditing(false);
              }}
            />
            <div className={cx(styles.cardFooterWrap, styles.footerOpen)}>
              <div className={styles.cardFooterInner}>
                <div className={styles.cardFooter}>
                  <div className={styles.footerActions}>
                    <button
                      className={styles.footerBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy(block.id);
                      }}
                    >
                      <i className="paperflite-copy" />
                    </button>
                    <button
                      className={cx(styles.footerBtn, { [styles.deleteDisabled]: disableDelete })}
                      disabled={disableDelete}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disableDelete) {
                          onDelete(block.id);
                        }
                      }}
                    >
                      <i className="paperflite-delete" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className={styles.cardHeader}>
              <span className={styles.slash}>/</span>
              <span className={styles.cardTitle}>
                {block.title.toUpperCase()}
              </span>
            </div>

            <div
              className={cx(styles.descWrap, { [styles.descOpen]: !titleOnly })}
            >
              <div className={styles.descInner}>
                <p className={styles.cardDesc}>{block.description}</p>
              </div>
            </div>

            <div
              className={cx(styles.cardFooterWrap, {
                [styles.footerOpen]: hovered && !titleOnly,
              })}
            >
              <div className={styles.cardFooterInner}>
                <div className={styles.cardFooter}>
                  <div className={styles.footerActions}>
                    <button
                      className={styles.footerBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        onCopy(block.id);
                      }}
                    >
                      <i className="paperflite-copy" />
                    </button>
                     <button
                      className={cx(styles.footerBtn, { [styles.deleteDisabled]: disableDelete })}
                      disabled={disableDelete}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!disableDelete) {
                          onDelete(block.id);
                        }
                      }}
                    >
                      <i className="paperflite-delete" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const SKEL_WIDTHS = ["45%", "52%", "62%", "48%", "35%", "50%"];

const SkeletonCard: React.FC<{ index: number }> = ({ index }) => (
  <div className={styles.skeletonCard} style={{ animationDelay: `${index * 0.07}s` }}>
    <div className={styles.skelBarTitle} style={{ width: SKEL_WIDTHS[index % SKEL_WIDTHS.length] }} />
  </div>
);

export const StoryLinePage: React.FC = () => {
  const location      = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as { prompt?: string; workspaceId?: string; listId?: string | null } | null;
  const prompt        = locationState?.prompt      ?? "";
  const workspaceId   = locationState?.workspaceId ?? "";
  const listId        = locationState?.listId      ?? null;

  const { promptData, setLoading, setPromptData, updateSectionOrder } = usePromptEditorContext();
  const { mutate: generateSection } = usePromptEditorSection();

  const [blocks,         setBlocks]         = useState<StoryBlock[]>([]);
  const [activeId,       setActiveId]       = useState<string | null>(null);
  const [insertingAfter, setInsertingAfter] = useState<number | null>(null);
  const [phase,          setPhase]          = useState<"skeleton" | "titles" | "full">("skeleton");
  const [isGenerating, setIsGenerating] = useState(false);

  const pendingPayloadRef = useRef<unknown>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // TEMP: origin check disabled.
      if (event.data?.type !== PROMPT_EDITOR_DATA_REQUEST) return;
      if (!pendingPayloadRef.current) return;
      const source = event.source as Window | null;
      if (!source) return;
      source.postMessage(
        { type: PROMPT_EDITOR_DATA, payload: pendingPayloadRef.current },
        "*",
      );
      pendingPayloadRef.current = null;
      navigate(
        listId
          ? `/experiences/${workspaceId}/storyboards/folder/${listId}`
          : `/experiences/${workspaceId}/storyboards`,
      );
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!prompt || !workspaceId) return;
    setLoading(true);
    setPhase("skeleton");

    generateSection(
      { request: { prompt }, workspaceId },
      {
        onSuccess: (data) => {
          setPromptData(data);
          const sections = data.pageStructure?.sections;
          const loaded: StoryBlock[] = Array.isArray(sections) ? sections.map(sectionToBlock) : [];
          setBlocks(loaded);
          setPhase("titles");
          setTimeout(() => setPhase("full"), 900);
        },
        onError: () => setPhase("full"),
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGenerate = useCallback(() => {
    if (!promptData || !workspaceId) return;

    setIsGenerating(true);

    const templateId = BLANK_TEMPLATE_IDS[STORYBOARD_TYPES.LANDING_PAGE];

    if (!templateId) {
      setIsGenerating(false);
      return;
    }

    const storyboardName = _.get(
      promptData,
      "storyboardName",
      "prompt based editor",
    ) as string;

    const updatedPromptData = {
      ...promptData,
      pageStructure: {
        ...promptData.pageStructure,
        sections: blocks.map(blockToSection),
      },
    };

    pendingPayloadRef.current = updatedPromptData;

    const targetPath = buildCreationTargetPath(
      workspaceId,
      templateId,
      storyboardName,
      listId,
      true,
    );

    const redirectUrl = buildCleverstoryUrl(targetPath);

    if (!redirectUrl) {
      setIsGenerating(false);
      return;
    }

    const win = window.open(redirectUrl, "_blank");
    win?.focus();
  }, [promptData, workspaceId, listId, blocks]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const handleDelete = useCallback((id: string) => {
    setBlocks((prev) => prev.filter((b) => b.id !== id));
  }, []);

  const handleCopy = useCallback((id: string) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === id);
      if (idx === -1) return prev;
      const copy = { ...prev[idx], id: newId(), title: prev[idx].title + " (copy)", isNew: true };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  }, []);

  const handleUpdate = useCallback((id: string, title: string, desc: string) => {
    setBlocks((prev) => prev.map((b) => (b.id === id ? { ...b, title, description: desc } : b)));
  }, []);

  const handleAddBlock = useCallback((afterIndex: number, title: string, desc: string) => {
    setBlocks((prev) => {
      const next = [...prev];
      next.splice(afterIndex + 1, 0, { id: newId(), title, description: desc, isNew: true });
      return next;
    });
    setInsertingAfter(null);
  }, []);

  const handleDragStart = (e: DragStartEvent) => setActiveId(String(e.active.id));

  const handleDragEnd = useCallback((e: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;

    setBlocks((prev) => {
      const oldIdx = prev.findIndex((b) => b.id === active.id);
      const newIdx = prev.findIndex((b) => b.id === over.id);
      const reordered = arrayMove(prev, oldIdx, newIdx);

      updateSectionOrder(reordered.map(blockToSection));

      return reordered;
    });
  }, [updateSectionOrder]);

  const activeBlock = blocks.find((b) => b.id === activeId) ?? null;
  const isCentered  = phase === "skeleton" || phase === "titles";

  return (
    <div
      className={cx(
        styles.page,
        isCentered && styles.pageCentered,
        isGenerating && styles.pageLocked,
      )}
    >
      {phase === "skeleton" && (
        <div className={styles.blockList}>
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} index={i} />
          ))}
        </div>
      )}

      {phase !== "skeleton" && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis, restrictToWindowEdges]}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
            <div className={styles.blockList}>
              {blocks.map((block, idx) => (
                <React.Fragment key={block.id}>
                  {idx > 0 && phase === "full" && (
                    insertingAfter === idx - 1 ? (
                      <InlineAdder
                        onConfirm={(t, d) => handleAddBlock(idx - 1, t, d)}
                        onCancel={() => setInsertingAfter(null)}
                      />
                    ) : (
                      <BetweenAdder onAdd={() => setInsertingAfter(idx - 1)} />
                    )
                  )}
                  <SortableBlock
                    block={block}
                    onDelete={handleDelete}
                    onCopy={handleCopy}
                    onUpdate={handleUpdate}
                    titleOnly={phase === "titles"}
                    disableDelete={blocks.length === 1}
                  />
                </React.Fragment>
              ))}

              {phase === "full" && (
                insertingAfter === blocks.length - 1 ? (
                  <InlineAdder
                    onConfirm={(t, d) => handleAddBlock(blocks.length - 1, t, d)}
                    onCancel={() => setInsertingAfter(null)}
                  />
                ) : (
                  <button className={styles.addNewBlockBtn} onClick={() => setInsertingAfter(blocks.length - 1)}>
                    <i className="paperflite-add" /> Add new block
                  </button>
                )
              )}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeBlock && (
              <SortableBlock block={activeBlock} onDelete={() => {}} onCopy={() => {}} onUpdate={() => {}} isDragOverlay />
            )}
          </DragOverlay>
        </DndContext>
      )}

      {phase === "full" && blocks.length > 0 && (
        <div className={styles.generateBar}>
          <button
            className={styles.generateBtn}
            onClick={handleGenerate}
            disabled={isGenerating}
          >
            {isGenerating ? "Generating..." : "Generate design"}
            <img src="/images/aiNew.svg" alt="aiNew"/>
          </button>
        </div>
      )}
    </div>
  );
};